import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Input, Space, Tooltip, Select } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { DateRangePicker } from '../../input-date-range/styles'
import { datesToMoment, datesToString } from './utils'
import { InputInteger, InputPhone } from './elements'
import InputText from '../../input-text'
import { useLocation } from 'react-router-dom'
import type { FormInstance } from 'antd'
import type { filtersType } from './interfaces'

type TFilterValue = undefined | number | string | readonly string[]

function FiltersList({
  beforeElement = null,
  filters = [],
  onChange,
  isChangeByButtons = false,
  form,
}: {
  beforeElement?: React.ReactNode
  filters: filtersType[]
  onChange: any
  isChangeByButtons?: boolean
  form: FormInstance
}) {
  const location = useLocation()
  const [values, setValues] = useState<any>(
    filters.reduce((obj, cur) => ({ ...obj, [cur.name]: cur.value }), {}),
  )
  const isUnmounted = useRef(false)

  // Синхронизация values с фильтрами из URL (при навигации по ссылке с query-параметрами)
  useEffect(() => {
    const newValues = filters.reduce((obj, cur) => ({ ...obj, [cur.name]: cur.value }), {})
    setValues(prev => ({ ...prev, ...newValues }))
  }, [filters])

  useEffect(() => {
    if (!isChangeByButtons) {
      return
    }
    const urlSearchParams = new URLSearchParams(location.search)
    const stageUrlParams = urlSearchParams.get('stage')?.split(',').filter(Boolean)
    const taskTypeParams = urlSearchParams.get('task_type')?.split(',').filter(Boolean)
    onChangeHandler({ stage: stageUrlParams, task_type: taskTypeParams }, false)
  }, [location, isChangeByButtons])

  const onChangeHandler = useCallback(
    (current, isUpd = true) => {
      setValues(prevValues => {
        const newState = { ...prevValues, ...current }

        if (isUpd) {
          onChange(
            Object.fromEntries(Object.entries(newState).filter(([, value]) => value !== undefined)),
          )
        }

        return newState
      })
    },
    [onChange],
  )

  useEffect(() => {
    if (isUnmounted.current) {
      return
    }
    const timer = window.setTimeout(() => {
      form.setFieldsValue(values)
    }, 0)
    return () => {
      window.clearTimeout(timer)
    }
  }, [values, form])

  useEffect(
    () => () => {
      isUnmounted.current = true
    },
    [],
  )

  /**
   * Возврачает инпут, в соответствии с входящими параметрами
   */
  const getInput = (
    { type, options, placeholder, name, disabled, items, value, style, prefix }: filtersType,
    currentValue: TFilterValue,
  ) => {
    const correctStyle = { width: 200, ...style }
    if (type === 'filter_group' && items?.length) {
      return items.map((item, idx) => (
        <React.Fragment key={item.name || idx}>{getInput(item, currentValue)}</React.Fragment>
      ))
    }
    if (type === 'input-string') {
      const stringValue = typeof currentValue === 'string' ? currentValue : ''
      const applyValue = (value?: string) => {
        onChangeHandler({ [name]: value || undefined })
      }
      const hasMask = options?.displayFormat && options?.mask

      if (hasMask) {
        const schema = {
          type: 'string',
          options: { displayFormat: options.displayFormat, mask: options.mask },
        }
        return (
          <Tooltip title={options?.tooltip}>
            <Space.Compact style={correctStyle} block>
              <InputText
                value={stringValue}
                onChange={v => onChangeHandler({ [name]: v || undefined }, false)}
                schema={schema}
              />
              <Button
                icon={<SearchOutlined />}
                onClick={() => applyValue(stringValue || undefined)}
                style={{ borderColor: '#999999' }}
              />
            </Space.Compact>
          </Tooltip>
        )
      }

      return (
        <Tooltip title={options?.tooltip}>
          <Space.Compact style={correctStyle} block>
            <Input
              allowClear
              placeholder={(placeholder as string) || 'Заполните'}
              value={stringValue}
              onChange={event =>
                onChangeHandler({ [name]: event.target.value || undefined }, false)
              }
              onPressEnter={event => applyValue(event.currentTarget.value || undefined)}
            />
            <Button
              icon={<SearchOutlined />}
              onClick={() => applyValue(stringValue || undefined)}
              style={{ borderColor: '#999999' }}
            />
          </Space.Compact>
        </Tooltip>
      )
    }
    if (type === 'input-integer') {
      return (
        <Tooltip title={options?.tooltip}>
          <div>
            <InputInteger
              value={typeof currentValue === 'number' ? currentValue : undefined}
              prefix={prefix}
              placeholder={Array.isArray(placeholder) ? placeholder[0] : placeholder}
              onSearch={(v?: number) => onChangeHandler({ [name]: v ?? undefined })}
              onChange={(newValue?: number) =>
                onChangeHandler({ [name]: newValue ?? undefined }, false)
              }
            />
          </div>
        </Tooltip>
      )
    }
    if (type === 'phone') {
      const applyPhoneValue = (v?: string) => {
        onChangeHandler({ [name]: v || undefined })
      }
      return (
        <Tooltip title={options?.tooltip}>
          <div>
            <InputPhone
              value={typeof currentValue === 'string' ? currentValue : undefined}
              onChange={(v: TFilterValue) => onChangeHandler({ [name]: v || undefined }, false)}
              onSearch={applyPhoneValue}
            />
          </div>
        </Tooltip>
      )
    }
    if (type === 'select') {
      return (
        <Tooltip title={options?.tooltip}>
          <Select
            allowClear={options?.allowClear !== undefined ? options.allowClear : true}
            placeholder={placeholder || 'Выберите'}
            value={currentValue ?? value}
            options={options?.enum || []}
            disabled={disabled || false}
            style={correctStyle}
            onChange={(v: TFilterValue) => onChangeHandler({ [name]: v })}
          />
        </Tooltip>
      )
    }
    if (type === 'select_multiple') {
      return (
        <Tooltip title={options?.tooltip}>
          <Select
            allowClear={options?.allowClear !== undefined ? options.allowClear : true}
            placeholder={placeholder || 'Выберите'}
            value={currentValue}
            options={options?.enum || []}
            disabled={disabled || false}
            style={correctStyle}
            onChange={(v: TFilterValue) => onChangeHandler({ [name]: v || undefined })}
            mode="multiple"
            maxTagCount={4}
          />
        </Tooltip>
      )
    }
    if (type === 'dates') {
      return (
        <Tooltip title={options?.tooltip}>
          <DateRangePicker
            key={`${name}-${value || 'empty'}`}
            value={datesToMoment(currentValue || value)}
            placeholder={placeholder as [string, string] | undefined}
            style={correctStyle}
            onChange={v => onChangeHandler({ [name]: datesToString(v) })}
          />
        </Tooltip>
      )
    }
    return null
  }

  /**
   * Формирует список для вывода
   */
  const filtersList = useMemo(() => {
    return filters.map(item => {
      return {
        render: getInput(item, values?.[item.name]),
        name: item.name,
        key: item.name,
        style: item?.style,
      }
    })
  }, [filters, values])

  return (
    <Form form={form} component={false}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          position: 'relative',
          width: '100%',
        }}
      >
        {beforeElement}
        {filtersList.map(({ render, name, key, style }) => (
          <Form.Item key={key} name={name} style={{ ...style, marginBottom: 3 }}>
            {render}
          </Form.Item>
        ))}
      </div>
    </Form>
  )
}

export default FiltersList
